import { forwardRef } from 'react';
import { getCardBadge } from '../utils/deckUtils';

const FlashCard = forwardRef(({
  currentCard,
  currentIndex,
  deckLength,
  isFlipped,
  isDragging,
  dragX,
  swipeDirection,
  isDarkMode,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}, ref) => {
  // Calculate visual feedback
  const rotation = dragX * 0.1;
  const opacity = Math.max(0.5, 1 - Math.abs(dragX) / 300);

  const getSwipeIndicator = () => {
    if (dragX > 50) return { text: '记住了', color: '#22c55e', opacity: Math.min(1, (dragX - 50) / 100) };
    if (dragX < -50) return { text: '再学习', color: '#ef4444', opacity: Math.min(1, (-dragX - 50) / 100) };
    return null;
  };

  const indicator = getSwipeIndicator();
  const badge = getCardBadge(currentCard);
  const isCustomCard = currentCard.deckId === 'custom';
  const isPhrase = currentCard.deckId === 'connectors';

  // Custom cards get golden styling
  const badgeBgColor = isCustomCard ? `${badge.color}40` : `${badge.color}20`;
  const badgeBorderColor = isCustomCard ? `${badge.color}80` : `${badge.color}40`;

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        cursor: 'grab',
        touchAction: 'none',
        transformStyle: 'preserve-3d',
        transition: isDragging ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
        transform: swipeDirection
          ? `translateX(${swipeDirection === 'right' ? '400px' : '-400px'}) rotate(${swipeDirection === 'right' ? '30deg' : '-30deg'})`
          : `translateX(${dragX}px) rotate(${rotation}deg) ${isFlipped ? 'rotateY(180deg)' : ''}`,
        opacity: swipeDirection ? 0 : opacity,
      }}
    >
      {/* Front of card */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        background: isCustomCard
          ? 'linear-gradient(145deg, #fffbeb, #fef3c7)'
          : 'linear-gradient(145deg, #f8fafc, #e2e8f0)',
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: isCustomCard
          ? '0 25px 80px rgba(255,215,0,0.3), inset 0 1px 0 rgba(255,255,255,0.8)'
          : '0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.8)',
        border: isCustomCard
          ? '2px solid rgba(255,215,0,0.4)'
          : '1px solid rgba(255,255,255,0.3)',
      }}>
        {/* Swipe Indicator Overlay */}
        {indicator && (
          <div style={{
            position: 'absolute',
            top: '20px',
            left: dragX > 0 ? '20px' : 'auto',
            right: dragX < 0 ? '20px' : 'auto',
            padding: '8px 16px',
            background: indicator.color,
            color: 'white',
            borderRadius: '8px',
            fontSize: '18px',
            fontWeight: 'bold',
            opacity: indicator.opacity,
            transform: `rotate(${dragX > 0 ? '-15deg' : '15deg'})`,
            border: '3px solid white',
          }}>
            {indicator.text}
          </div>
        )}

        {/* Character */}
        <div className={isPhrase ? 'card-char-front-phrase' : 'card-char-front'} style={{
          fontWeight: '400',
          color: '#0f172a',
          lineHeight: isPhrase ? 1.2 : 1,
          textShadow: '2px 2px 0 rgba(0,0,0,0.05)',
          padding: isPhrase ? '0 16px' : 0,
          textAlign: 'center',
        }}>
          {currentCard.char}
        </div>

        {/* Deck badge */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: badgeBgColor,
          border: `1px solid ${badgeBorderColor}`,
          borderRadius: '12px',
          boxShadow: isCustomCard ? '0 2px 8px rgba(255,215,0,0.3)' : 'none',
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: badge.color,
          }}>{badge.label}</span>
          {!isCustomCard && (
            <span style={{
              fontSize: '10px',
              color: '#64748b',
            }}>#{currentCard.originalIndex + 1}</span>
          )}
        </div>

        {/* Rank badge */}
        <div style={{
          position: 'absolute',
          bottom: '24px',
          padding: '6px 16px',
          background: isCustomCard
            ? 'linear-gradient(135deg, #f59e0b, #d97706)'
            : 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          color: 'white',
          borderRadius: '20px',
          fontSize: '13px',
          fontWeight: '600',
          letterSpacing: '0.5px',
        }}>
          {currentIndex + 1} / {deckLength}
        </div>

        {/* Tap hint */}
        <div style={{
          position: 'absolute',
          bottom: '70px',
          color: '#94a3b8',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <span style={{ fontSize: '16px' }}>👆</span> 点击翻转
        </div>
      </div>

      {/* Back of card */}
      <div style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        backfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)',
        background: isCustomCard
          ? (isDarkMode
            ? 'linear-gradient(145deg, #422006, #1c0a00)'
            : 'linear-gradient(145deg, #fffbeb, #fef3c7)')
          : (isDarkMode
            ? 'linear-gradient(145deg, #0f172a, #1e293b)'
            : 'linear-gradient(145deg, #f0f9f8, #e0f2f1)'),
        borderRadius: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        boxShadow: isCustomCard
          ? (isDarkMode
            ? '0 25px 80px rgba(255,215,0,0.2), inset 0 1px 0 rgba(255,215,0,0.2)'
            : '0 25px 80px rgba(255,215,0,0.3), inset 0 1px 0 rgba(255,255,255,0.8)')
          : (isDarkMode
            ? '0 25px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
            : '0 25px 80px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.8)'),
        border: isCustomCard
          ? `2px solid ${isDarkMode ? 'rgba(255,215,0,0.4)' : 'rgba(255,215,0,0.5)'}`
          : (isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'),
      }}>
        {/* Badge on back */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 10px',
          background: isCustomCard ? `${badge.color}50` : `${badge.color}30`,
          border: `1px solid ${isCustomCard ? badge.color : `${badge.color}50`}`,
          borderRadius: '12px',
          boxShadow: isCustomCard ? '0 2px 8px rgba(255,215,0,0.3)' : 'none',
        }}>
          <span style={{
            fontSize: '11px',
            fontWeight: '700',
            color: badge.color,
          }}>{badge.label}</span>
          {!isCustomCard && (
            <span style={{
              fontSize: '10px',
              color: '#94a3b8',
            }}>#{currentCard.originalIndex + 1}</span>
          )}
        </div>

        {/* Character (smaller on back) */}
        <div className={isPhrase ? 'card-char-back-phrase' : 'card-char-back'} style={{
          color: isCustomCard
            ? (isDarkMode ? '#fcd34d' : '#b45309')
            : (isDarkMode ? '#f8fafc' : '#1a1a2e'),
          marginBottom: '12px',
          lineHeight: isPhrase ? 1.2 : 1,
          textAlign: 'center',
          padding: isPhrase ? '0 8px' : 0,
        }}>
          {currentCard.char}
        </div>

        {/* Pinyin */}
        <div className="card-pinyin" style={{
          color: isCustomCard
            ? (isDarkMode ? '#fbbf24' : '#d97706')
            : (isDarkMode ? '#3b82f6' : '#4FAEA5'),
          fontWeight: '500',
          marginBottom: '8px',
          fontStyle: 'italic',
        }}>
          {currentCard.pinyin}
        </div>

        {/* Meaning */}
        <div className="card-meaning" style={{
          color: isCustomCard
            ? (isDarkMode ? '#fef3c7' : '#78350f')
            : (isDarkMode ? '#e2e8f0' : '#374151'),
          textAlign: 'center',
          marginBottom: '16px',
          lineHeight: 1.4,
          maxWidth: '100%',
        }}>
          {currentCard.meaning}
        </div>

        {/* Divider */}
        <div style={{
          width: '60%',
          height: '1px',
          background: isCustomCard
            ? (isDarkMode
              ? 'linear-gradient(90deg, transparent, #fbbf24, transparent)'
              : 'linear-gradient(90deg, transparent, #d97706, transparent)')
            : (isDarkMode
              ? 'linear-gradient(90deg, transparent, #475569, transparent)'
              : 'linear-gradient(90deg, transparent, #9ca3af, transparent)'),
          marginBottom: '16px',
        }} />

        {/* Example */}
        {currentCard.example && (
          <div className="card-example" style={{
            color: isCustomCard
              ? (isDarkMode ? '#fcd34d' : '#92400e')
              : (isDarkMode ? '#94a3b8' : '#6b7280'),
            textAlign: 'center',
            lineHeight: 1.5,
            padding: '0 4px',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <span style={{ color: isCustomCard ? '#f59e0b' : '#22c55e', fontWeight: '500' }}>例:</span> {currentCard.example}
          </div>
        )}
      </div>
    </div>
  );
});

FlashCard.displayName = 'FlashCard';

export default FlashCard;
